import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useRef, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subDays, startOfDay, format } from "date-fns";

export type TimeframeOption = "1d" | "7d" | "30d" | "all";

const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute

interface AnalyticsStats {
  totalPageViews: number;
  uniqueUsers: number;
  avgPagesPerSession: number;
  totalSessions: number;
  totalClicks: number;
  totalFeatureUsage: number;
  mobileUsers: number;
  desktopUsers: number;
}

interface PageViewData {
  date: string;
  count: number;
}

interface TopPage {
  target: string;
  count: number;
}

interface FeatureUsageData {
  target: string;
  count: number;
}

interface RecentInteraction {
  id: string;
  user_id: string | null;
  session_id: string;
  interaction_type: string;
  target: string;
  metadata: Record<string, unknown>;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

const getStartDate = (timeframe: TimeframeOption): Date | null => {
  const now = new Date();
  switch (timeframe) {
    case "1d":
      return startOfDay(subDays(now, 1));
    case "7d":
      return startOfDay(subDays(now, 7));
    case "30d":
      return startOfDay(subDays(now, 30));
    case "all":
      return null;
  }
};

export const useAdminAnalytics = (timeframe: TimeframeOption = "7d") => {
  const startDate = getStartDate(timeframe);
  const hasTriggeredRefresh = useRef(false);
  const lastFetchedAtRef = useRef<Date | null>(null);

  // Fetch analytics stats
  const statsQuery = useQuery({
    queryKey: ["admin-analytics-stats", timeframe],
    queryFn: async (): Promise<AnalyticsStats> => {
      let interactionsQuery = supabase
        .from("user_interactions")
        .select("id, interaction_type, user_id, session_id", { count: "exact" });

      let sessionsQuery = supabase
        .from("user_sessions")
        .select("id, user_id, page_count, device_info", { count: "exact" });

      if (startDate) {
        const startDateStr = startDate.toISOString();
        interactionsQuery = interactionsQuery.gte("created_at", startDateStr);
        sessionsQuery = sessionsQuery.gte("started_at", startDateStr);
      }

      const [interactionsResult, sessionsResult] = await Promise.all([
        interactionsQuery,
        sessionsQuery,
      ]);

      if (interactionsResult.error) throw interactionsResult.error;
      if (sessionsResult.error) throw sessionsResult.error;

      const interactions = interactionsResult.data || [];
      const sessions = sessionsResult.data || [];

      const pageViews = interactions.filter((i) => i.interaction_type === "page_view").length;
      const clicks = interactions.filter((i) => i.interaction_type === "click").length;
      const featureUsage = interactions.filter((i) => i.interaction_type === "feature_usage").length;
      
      const uniqueUserIds = new Set(
        interactions.map((i) => i.user_id).filter(Boolean)
      );

      const totalPageCount = sessions.reduce((sum, s) => sum + (s.page_count || 0), 0);
      const avgPagesPerSession = sessions.length > 0 ? totalPageCount / sessions.length : 0;

      const mobileUsers = sessions.filter(
        (s) => (s.device_info as Record<string, unknown>)?.isMobile === true
      ).length;

      lastFetchedAtRef.current = new Date();

      return {
        totalPageViews: pageViews,
        uniqueUsers: uniqueUserIds.size,
        avgPagesPerSession: Math.round(avgPagesPerSession * 10) / 10,
        totalSessions: sessions.length,
        totalClicks: clicks,
        totalFeatureUsage: featureUsage,
        mobileUsers,
        desktopUsers: sessions.length - mobileUsers,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  // Fetch page views over time
  const pageViewsChartQuery = useQuery({
    queryKey: ["admin-analytics-pageviews-chart", timeframe],
    queryFn: async (): Promise<PageViewData[]> => {
      let query = supabase
        .from("user_interactions")
        .select("created_at")
        .eq("interaction_type", "page_view")
        .order("created_at", { ascending: true });

      if (startDate) {
        query = query.gte("created_at", startDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group by date
      const grouped: Record<string, number> = {};
      (data || []).forEach((item) => {
        const date = format(new Date(item.created_at), "yyyy-MM-dd");
        grouped[date] = (grouped[date] || 0) + 1;
      });

      return Object.entries(grouped).map(([date, count]) => ({
        date,
        count,
      }));
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  // Fetch top pages
  const topPagesQuery = useQuery({
    queryKey: ["admin-analytics-top-pages", timeframe],
    queryFn: async (): Promise<TopPage[]> => {
      let query = supabase
        .from("user_interactions")
        .select("target")
        .eq("interaction_type", "page_view");

      if (startDate) {
        query = query.gte("created_at", startDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      // Count occurrences
      const counts: Record<string, number> = {};
      (data || []).forEach((item) => {
        counts[item.target] = (counts[item.target] || 0) + 1;
      });

      return Object.entries(counts)
        .map(([target, count]) => ({ target, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  // Fetch feature usage
  const featureUsageQuery = useQuery({
    queryKey: ["admin-analytics-feature-usage", timeframe],
    queryFn: async (): Promise<FeatureUsageData[]> => {
      let query = supabase
        .from("user_interactions")
        .select("target")
        .eq("interaction_type", "feature_usage");

      if (startDate) {
        query = query.gte("created_at", startDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      // Count occurrences
      const counts: Record<string, number> = {};
      (data || []).forEach((item) => {
        counts[item.target] = (counts[item.target] || 0) + 1;
      });

      return Object.entries(counts)
        .map(([target, count]) => ({ target, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  // Fetch recent interactions with user info
  const recentInteractionsQuery = useQuery({
    queryKey: ["admin-analytics-recent", timeframe],
    queryFn: async (): Promise<RecentInteraction[]> => {
      // Fetch all interactions in 1000-row chunks to bypass Supabase row limit
      let allData: any[] = [];
      let from = 0;
      const CHUNK_SIZE = 1000;

      while (true) {
        let query = supabase
          .from("user_interactions")
          .select("*")
          .order("created_at", { ascending: false })
          .range(from, from + CHUNK_SIZE - 1);

        if (startDate) {
          query = query.gte("created_at", startDate.toISOString());
        }

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;

        allData = allData.concat(data);
        if (data.length < CHUNK_SIZE) break;
        from += CHUNK_SIZE;
      }

      // Get unique user IDs to fetch profile info
      const userIds = [...new Set(allData.map((i) => i.user_id).filter(Boolean))];
      
      let profiles: Record<string, { email: string; first_name: string; last_name: string }> = {};
      if (userIds.length > 0) {
        // Batch profile lookups in chunks of 50 to avoid URL length limits
        for (let i = 0; i < userIds.length; i += 50) {
          const chunk = userIds.slice(i, i + 50) as string[];
          const { data: profileData } = await supabase
            .from("profiles")
            .select("id, email, first_name, last_name")
            .in("id", chunk);

          if (profileData) {
            profileData.forEach((p) => {
              profiles[p.id] = { email: p.email || "", first_name: p.first_name || "", last_name: p.last_name || "" };
            });
          }
        }
      }

      return allData.map((item) => ({
        ...item,
        metadata: item.metadata as Record<string, unknown>,
        user_email: item.user_id ? profiles[item.user_id]?.email : undefined,
        user_name: item.user_id
          ? `${profiles[item.user_id]?.first_name || ""} ${profiles[item.user_id]?.last_name || ""}`.trim()
          : undefined,
      }));
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  const refetch = useCallback(() => {
    statsQuery.refetch();
    pageViewsChartQuery.refetch();
    topPagesQuery.refetch();
    featureUsageQuery.refetch();
    recentInteractionsQuery.refetch();
  }, [statsQuery, pageViewsChartQuery, topPagesQuery, featureUsageQuery, recentInteractionsQuery]);

  // Auto-refresh if data is stale on mount
  useEffect(() => {
    if (statsQuery.isLoading || hasTriggeredRefresh.current) return;
    
    const lastFetched = lastFetchedAtRef.current;
    if (!lastFetched) return;
    
    const isStale = Date.now() - lastFetched.getTime() > STALE_THRESHOLD_MS;
    if (isStale) {
      hasTriggeredRefresh.current = true;
      refetch();
    }
  }, [statsQuery.isLoading, refetch]);

  // Reset refresh flag on timeframe change
  useEffect(() => {
    hasTriggeredRefresh.current = false;
  }, [timeframe]);

  const isRefreshing = 
    (statsQuery.isFetching && !statsQuery.isLoading) ||
    (pageViewsChartQuery.isFetching && !pageViewsChartQuery.isLoading) ||
    (topPagesQuery.isFetching && !topPagesQuery.isLoading) ||
    (featureUsageQuery.isFetching && !featureUsageQuery.isLoading) ||
    (recentInteractionsQuery.isFetching && !recentInteractionsQuery.isLoading);

  return {
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    pageViewsChart: pageViewsChartQuery.data || [],
    isLoadingPageViewsChart: pageViewsChartQuery.isLoading,
    topPages: topPagesQuery.data || [],
    isLoadingTopPages: topPagesQuery.isLoading,
    featureUsage: featureUsageQuery.data || [],
    isLoadingFeatureUsage: featureUsageQuery.isLoading,
    recentInteractions: recentInteractionsQuery.data || [],
    isLoadingRecentInteractions: recentInteractionsQuery.isLoading,
    refetch,
    lastFetchedAt: lastFetchedAtRef.current,
    isRefreshing,
  };
};
